import apiService from './api';
import type { ApiResponse } from '../types';

/**
 * Moving cultivars registered under the wrong organisation. Platform super
 * admin only (HHNodeServer `/api/platform/cultivar-move`, cultivarMoveService).
 * The server decides what can move and what stays; the preview is the move run
 * and rolled back.
 */

export interface MoveOrganisation {
  id: number;
  name: string;
  is_active: boolean;
  cultivar_count: number;
}

export interface MovableCultivar {
  id: number;
  name: string;
  legacy_code: string | null;
  protection_status: string | null;
  created_at: string;
  crop_type_name: string | null;
}

export interface MoveRequest {
  fromOrgId: number;
  toOrgId: number;
  ids: number[];
  moveRelated: boolean;
}

export interface MoveResult {
  from: { id: number; name: string };
  to: { id: number; name: string };
  moved: { id: number; name: string }[];
  blocked: { id: number; name: string | null; reasons: string[] }[];
  programs_unlinked: number;
  /** The old organisation's own records about the cultivars; moved when move_related. */
  related: { table: string; label: string; rows: number }[];
  /** What the old organisation keeps that names the cultivars. */
  left_behind: { table: string; label: string; rows: number; cultivars: number }[];
  move_related: boolean;
}

const body = (r: MoveRequest) => ({
  from_org_id: r.fromOrgId,
  to_org_id: r.toOrgId,
  ids: r.ids,
  move_related: r.moveRelated,
});

class CultivarMoveService {
  async organisations(): Promise<ApiResponse<MoveOrganisation[]>> {
    return apiService.get('/platform/cultivar-move/organisations');
  }

  async cultivars(orgId: number): Promise<ApiResponse<MovableCultivar[]>> {
    return apiService.get(`/platform/cultivar-move/cultivars?org_id=${orgId}`);
  }

  async preview(r: MoveRequest): Promise<ApiResponse<MoveResult>> {
    return apiService.post('/platform/cultivar-move/preview', body(r), undefined, { timeout: 60000 });
  }

  async move(r: MoveRequest): Promise<ApiResponse<MoveResult>> {
    return apiService.post('/platform/cultivar-move', body(r), undefined, { timeout: 60000 });
  }
}

export default new CultivarMoveService();
